import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Field, Form, FormSpy } from 'react-final-form';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { OnChange } from 'react-final-form-listeners';
import moment from 'moment';
import {
  Box,
  Button,
  TextAreaInput,
  TextInput,
  StyledComponent,
  BoxProps,
  withDefaults, ActionConfirmation,
} from '@qwealth/qcore';
import { fetchCurrentAddress, selectUser } from '@qwealth/qdata';
import { RootState, useAppDispatch } from 'data/store';

import {
  createCalendarEvent,
  createCalendarHouseholdEvent,
  deleteEvent,
  updateEvent,
} from 'data/actions/microsoft/calendar';
import { createHouseholdEvent, deleteHouseholdActivity, updateHouseholdEvent } from 'data/actions/householdActivities';
import { CalendarEvent } from 'data/data-layer/event';
import { selectHouseholdMetadata } from 'data/selectors/householdSelectors';
import { deleteCalendarEvent, getAccessibleCalendars, findSharedCalendarId } from 'services/microsoft/graphService';
import { errorHandler } from 'services/axiosService';
import FromOrganizer from 'components/common/FromOrganizer';
import HouseholdSearch from 'components/common/HouseholdSearch';
import ContactSelect from '../ContactSelect';
import { DateTimeStyle } from '../Styling';
import Recurrence from './recurrence';
import {
  buildOfficeLocations,
  ConditionValue,
  FIELD_SUBSCRIPTION,
  getLocations,
  isEventReady,
  TITLES,
} from './helper';

const Label = withDefaults<BoxProps>({
  as: 'label',
  display: 'grid',
  gridGap: 'small',
})(Box) as StyledComponent<BoxProps, HTMLLabelElement>;

interface Props {
  context: 'Household' | 'QCal';
  close: () => void;
  event: CalendarEvent;
}

const AddEvent: FC<Props> = ({ event, close, context}): ReactElement => {
  const dispatch = useAppDispatch();

  const household = useSelector(selectHouseholdMetadata);
  const { partner: userPartner } = useSelector((state: RootState) => state.auth);
  const user = useSelector(selectUser);

  const isHousehold = context === 'Household';
  const isCalendar = context === 'QCal';

  const officeLocations = useMemo(() => buildOfficeLocations(userPartner), [userPartner]);
  const addresses = useMemo(
    () => getLocations(isHousehold ? household : null),
    [isHousehold, household],
  );

  const [selectedHouseholdCurrentAddress, setSelectedHouseholdCurrentAddress] = useState('');
  const [from, setFrom] = useState<string>(user.email);

  const getSelectedHouseholdCurrentAddress = async (QID: string) => {
    try {
      const clientAddress = await fetchCurrentAddress(QID);
      if (clientAddress !== undefined) {
        const { streetAddress, city, provinceOrState, postal } = clientAddress;
        setSelectedHouseholdCurrentAddress(
          `${streetAddress} ${city}, ${provinceOrState} ${postal}`,
        );
      } else {
        setSelectedHouseholdCurrentAddress('Could not find Household address');
      }
    } catch (err) {
      console.log('error', err);
    }
  };

  useEffect(() => {
    if (event.selectedHousehold?.value && event.selectedHousehold?.value !== 'Not Related') {
      getSelectedHouseholdCurrentAddress(event.selectedHousehold.value);
    }
  }, [event.selectedHousehold]);

  const saveEvent = async values => {
    if (values.allDay) {
      values.start.dateTime = moment(values.start.dateTime).startOf('day');
      values.end.dateTime = moment(values.end.dateTime).add(1, 'days').startOf('day');
    }

    let fromCalendarId: string | undefined = undefined;
    let payload = values;
    if (from !== user.email || event.calendarId) {
      fromCalendarId = await getAccessibleCalendars().then(calendars => findSharedCalendarId(calendars, from));

      if (!fromCalendarId) {
        return errorHandler(dispatch, 'Cannot create event via sharable calendar.')();
      }

      payload = { ...payload, from: { emailAddress: fromCalendarId } };
    }

    const { selectedHousehold, id } = payload;
    if (isHousehold) {
      if (id) {
        dispatch(updateHouseholdEvent(payload));
      } else {
        await dispatch(
          createHouseholdEvent(
            payload,
            selectedHousehold.value,
            fromCalendarId,
          ),
        );
      }
    }

    if (isCalendar) {
      if (!id && selectedHousehold.value) {
        dispatch(createCalendarHouseholdEvent(payload, selectedHousehold.value, fromCalendarId));
      } else if (id) {
        dispatch(updateEvent(payload, fromCalendarId));
      } else {
        dispatch(createCalendarEvent(payload, fromCalendarId));
      }
    }

    close();
  };

  const hasOccurred: boolean = event.start.dateTime < moment();
  const disabled: boolean = !!event.id && hasOccurred;

  return (
    <Form<CalendarEvent> initialValues={event} onSubmit={saveEvent}>
      {({ handleSubmit, form }) => (
        <>
          <p>
            To send from a different email, please have the owner&nbsp;
            <a
              target="_blank"
              href="https://support.microsoft.com/en-us/office/allow-someone-else-to-manage-your-mail-and-calendar-41c40c04-3bd1-4d22-963a-28eafec25926"
            >
              grant delegate access
            </a>
            . You can override the selectable list of options by typing in an email and pressing
            enter. Please contact the system administrator if you require additional support.
          </p>
          <Box>
            {!isHousehold && (
              <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
                <Label textStyle="formLabel">
                  Household*
                  <Field name="selectedHousehold" subscription={FIELD_SUBSCRIPTION}>
                    {({ input }) => (
                      <HouseholdSearch
                        value={input.value}
                        onChange={(opt) => {
                          input.onChange(opt);
                          const attendees = opt.household.emails.map(email => ({ label: email, value: email }));
                          form.change('attendees', attendees);
                        }}
                      />
                    )}
                  </Field>
                </Label>
                <OnChange name="selectedHousehold">
                  {selectedHousehold => {
                    getSelectedHouseholdCurrentAddress(selectedHousehold.value);
                  }}
                </OnChange>
              </Box>
            )}

            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <>
                  <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
                    <Label textStyle="formLabel">
                      From*
                      {event.id ? (
                        // @ts-ignore
                        <TextInput disabled value={event.organizer.emailAddress.address} />
                      ) : (
                        <FromOrganizer from={from} setFrom={setFrom} />
                      )}
                    </Label>
                  </Box>

                  {values.selectedHousehold.value !== 'Not Related' && (
                    <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
                      <Label textStyle="formLabel">
                        Title*
                        <Field name="subject" subscription={FIELD_SUBSCRIPTION}>
                          {({ meta: { error }, input }) => (
                            <>
                              <CreatableSelect
                                {...input}
                                isClearable
                                placeholder="Select or type title..."
                                isDisabled={disabled}
                                options={TITLES.map(t => {
                                  const formattedTitle = `${t}: ${values?.selectedHousehold?.label}`;
                                  return { label: formattedTitle, value: formattedTitle };
                                })}
                              />
                              {error && <Box color="error">{error}</Box>}
                            </>
                          )}
                        </Field>
                      </Label>
                    </Box>
                  )}

                  <ConditionValue when="selectedHousehold" field="value" is="Not Related">
                    <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
                      <Label textStyle="formLabel">
                        Title*
                        <Field
                          name="subject"
                          parse={value => ({ label: value, value })}
                          subscription={FIELD_SUBSCRIPTION}
                        >
                          {({ meta: { error }, input }) => (
                            <>
                              <TextInput
                                {...input}
                                // @ts-ignore
                                value={values.subject ? values.subject.value : ''}
                                // @ts-ignore
                                disabled={disabled}
                              />
                              {error && <Box color="error">{error}</Box>}
                            </>
                          )}
                        </Field>
                      </Label>
                    </Box>
                  </ConditionValue>

                  <Box display="grid" gridAutoFlow="column" gridGap="default" my="large">
                    <Label textStyle="formLabel">
                      Start Time*
                      <Field name="start.dateTime" subscription={FIELD_SUBSCRIPTION}>
                        {({ input }) => (
                          <>
                            <DateTimeStyle
                              {...input}
                              timeFormat={!values.allDay}
                              onChange={startDateTime => {
                                if (!values.allDay) {
                                  const newEndDateTime = moment(startDateTime).add(30, 'minute');
                                  // @ts-ignore
                                  form.change('end.dateTime', newEndDateTime);
                                }
                                input.onChange(startDateTime);
                              }}
                              inputProps={{ disabled }}
                              timeConstraints={{ minutes: { step: 15, min: 0, max: 60 } }}
                            />
                          </>
                        )}
                      </Field>
                    </Label>

                    <Label textStyle="formLabel">
                      End Time*
                      <Field name="end.dateTime" subscription={FIELD_SUBSCRIPTION}>
                        {({ input }) => (
                          <>
                            <DateTimeStyle
                              {...input}
                              timeFormat={!values.allDay}
                              inputProps={{ disabled }}
                              timeConstraints={{ minutes: { step: 15, min: 0, max: 60 } }}
                            />
                            {moment(values.end.dateTime).isBefore(values.start.dateTime) && (
                              <Box color="error">End date/time must be after start date/time.</Box>
                            )}
                          </>
                        )}
                      </Field>
                    </Label>
                  </Box>
                </>
              )}
            </FormSpy>

            <Box display="flex" flexDirection="column">
              <Label textStyle="formLabel">
                All Day
              </Label>
              <Box width={100}>
                <Field name="allDay" component="input" type="checkbox" />
              </Box>
            </Box>

            <Recurrence disabled={disabled} />

            <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
              <Label textStyle="formLabel">
                Attendees
                <Field name="attendees" subscription={FIELD_SUBSCRIPTION}>
                  {({ input }) => (
                    <ContactSelect
                      {...input}
                      isMulti
                      // @ts-ignore
                      disabled={disabled}
                    />
                  )}
                </Field>
              </Label>
            </Box>

            <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
              <Label textStyle="formLabel">
                Location
                <Field
                  name="location.displayName"
                  disabled={disabled}
                  subscription={FIELD_SUBSCRIPTION}
                  parse={value => value}
                >
                  {({ meta: { error }, input }) => (
                    <>
                      <Select
                        {...input}
                        isDisabled={!!event.id}
                        options={addresses.map(location => ({
                          label: location,
                          value: location,
                        }))}
                      />
                      {error && <Box color="error">{error}</Box>}
                    </>
                  )}
                </Field>
              </Label>
            </Box>

            <FormSpy subscription={{ values: true }}>
              {({ values, form }) => (
                <>
                  <ConditionValue when="location.displayName" field="value" is="Web Conference">
                    <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
                      <Label textStyle="formLabel">
                        Online Meeting Link
                        <Field name="location.onlineMeetingURL" subscription={FIELD_SUBSCRIPTION}>
                          {({ meta: { error }, input }) => (
                            <>
                              <TextInput
                                {...input}
                                // @ts-ignore
                                disabled={disabled}
                              />
                              {error && <Box color="error">{error}</Box>}
                            </>
                          )}
                        </Field>
                      </Label>
                    </Box>
                  </ConditionValue>

                  <ConditionValue when="location.displayName" field="value" is="Office Location">
                    <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
                      <Label textStyle="formLabel">
                        Location Details
                        <Field
                          name="location.displayName.locationUri"
                          subscription={FIELD_SUBSCRIPTION}
                        >
                          {({ meta: { error }, input }) => (
                            <>
                              <CreatableSelect
                                {...input}
                                onChange={opt => {
                                  input.onChange(opt);

                                  if (officeLocations.map(o => o.value).includes(opt.value)) {
                                    const { attendees } = values;
                                    let attendeeList = [opt];
                                    if (attendees) {
                                      attendeeList = [opt, ...attendees];
                                    }
                                    form.change('attendees', attendeeList);
                                  }
                                }}
                                isClearable
                                isDisabled={disabled}
                                options={officeLocations}
                                formatCreateLabel={val => `Location ${val}`}
                              />
                              {error && <Box color="error">{error}</Box>}
                            </>
                          )}
                        </Field>
                      </Label>
                    </Box>
                  </ConditionValue>

                  <ConditionValue when="location.displayName" field="value" is="Client Location">
                    <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
                      <Label textStyle="formLabel">
                        Location Details
                        <Field
                          name="location.displayName.locationUri"
                          subscription={FIELD_SUBSCRIPTION}
                        >
                          {({ meta: { error }, input }) => (
                            <>
                              <TextInput
                                {...input}
                                placeholder={selectedHouseholdCurrentAddress || ''}
                                // @ts-ignore
                                disabled={disabled}
                              />
                              {error && <Box color="error">{error}</Box>}
                            </>
                          )}
                        </Field>
                      </Label>
                    </Box>
                  </ConditionValue>

                  <Box display="grid" gridAutoFlow="column" gridGap="default" mb="default">
                    {values.onlineMeetingProvider === 'teamsForBusiness' ? (
                      <div dangerouslySetInnerHTML={{ __html: values.body.content }} />
                    ) : (
                      <Label textStyle="formLabel">
                        Details
                        <Field name="body.content" subscription={FIELD_SUBSCRIPTION}>
                          {({ input }) => (
                            <TextAreaInput
                              // @ts-ignore
                              rows={6}
                              // @ts-ignore
                              disabled={disabled}
                              {...input}
                            />
                          )}
                        </Field>
                      </Label>
                    )}
                  </Box>
                </>
              )}
            </FormSpy>

            <Box display="flex" gap="default">
              <FormSpy<CalendarEvent> subscription={{ values: true }}>
                {({ values }) => (
                  <>
                    <Button disabled={disabled || !isEventReady(values)} onClick={handleSubmit}>
                      Save
                    </Button>
                    {event.id && (
                      <ActionConfirmation
                        ActionButtonRenderer={handler => (
                          <Button onClick={handler}>
                            Delete
                          </Button>
                        )}
                        actionHandler={() => {
                          deleteCalendarEvent(event.id!, event.calendarId).then(() => {
                            if (isCalendar) {
                              event.eventApi?.remove();
                              dispatch(deleteEvent(event.id!));
                            } else if (isHousehold) {
                              dispatch(deleteHouseholdActivity(event));
                            }
                            close();
                          });
                        }}
                        target={`delete "${event.subject.value}"`}
                      />
                    )}
                    <Button variant="outline" onClick={() => close()}>
                      Cancel
                    </Button>
                  </>
                )}
              </FormSpy>
            </Box>
          </Box>
        </>
      )}
    </Form>
  );
};

export default AddEvent;
